import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';
import { ErrorState } from '../components/feedback/ErrorState';
import { useAuth } from '../hooks/useAuth.js';
import {
  useMarkOwnershipDeleted,
  useMarkOwnershipKept,
  useMarkOwnershipReassigned,
  useOwnershipTransfers,
} from '../hooks/useOwnershipTransfers.js';
import type {
  OwnershipTransferRecord,
  OwnershipTransferResourceType,
  OwnershipTransferReviewStatus,
} from '../types/api.js';

/**
 * @graph-stack cortex-admin
 * @graph-domain HR
 * @graph-business 退職時 cortex リソース移譲レコード管理ページ。pipeline_permissions.admins 限定で、退職者が所有していた pipeline_config 等の自動移譲監査記録を一覧し、kept / reassigned / deleted で最終決着させる。
 * @graph-connects useAuth [calls] isPipelineAdmin チェック
 * @graph-connects useOwnershipTransfers [calls] 監査レコード一覧取得
 * @graph-connects useMarkOwnershipKept [calls] 「admin 所有のまま残す」確定
 * @graph-connects useMarkOwnershipReassigned [calls] 別 owner への再移譲
 * @graph-connects useMarkOwnershipDeleted [calls] 元 doc 削除確定
 * @graph-connects useToast [calls] 結果通知
 */
export const Route = createFileRoute('/ownership-transfers')({
  component: OwnershipTransfersPage,
});

/** @graph-connects none */
const REVIEW_STATUS_VARIANT: Record<
  OwnershipTransferReviewStatus,
  'default' | 'success' | 'warning' | 'error' | 'info'
> = {
  pending: 'warning',
  kept: 'info',
  reassigned: 'success',
  deleted: 'error',
};

/** @graph-connects none */
const REVIEW_STATUS_LABEL: Record<OwnershipTransferReviewStatus, string> = {
  pending: 'pending',
  kept: 'kept',
  reassigned: 'reassigned',
  deleted: 'deleted',
};

/** @graph-connects none */
const RESOURCE_TYPE_LABEL: Record<OwnershipTransferResourceType, string> = {
  pipeline_config: 'pipeline_config',
  pipeline_api_key: 'pipeline_api_key',
  db_credential: 'db_credential',
  mcp_user_auth: 'mcp_user_auth',
};

/** @graph-connects none */
const REASSIGNABLE_RESOURCE_TYPES: ReadonlySet<OwnershipTransferResourceType> = new Set([
  'pipeline_config',
]);

type ConfirmMode = 'kept' | 'reassigned' | 'deleted';

interface ConfirmTarget {
  readonly mode: ConfirmMode;
  readonly record: OwnershipTransferRecord;
}

/** @graph-connects none */
function OwnershipTransfersPage() {
  const { t } = useTranslation();
  const { user, isLoading: isAuthLoading } = useAuth();
  const [originalOwnerEmail, setOriginalOwnerEmail] = useState('');
  const [reviewStatus, setReviewStatus] = useState<OwnershipTransferReviewStatus | ''>('pending');
  const [resourceType, setResourceType] = useState<OwnershipTransferResourceType | ''>('');
  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget | null>(null);
  const [newOwnerEmailInput, setNewOwnerEmailInput] = useState('');
  const { showToast } = useToast();

  const filter = useMemo(
    () => ({
      originalOwnerEmail: originalOwnerEmail.trim() === '' ? undefined : originalOwnerEmail.trim(),
      reviewStatus: reviewStatus === '' ? undefined : reviewStatus,
      resourceType: resourceType === '' ? undefined : resourceType,
    }),
    [originalOwnerEmail, reviewStatus, resourceType],
  );

  const { data, isLoading, isError, refetch } = useOwnershipTransfers(filter);
  const markKept = useMarkOwnershipKept();
  const markReassigned = useMarkOwnershipReassigned();
  const markDeleted = useMarkOwnershipDeleted();
  const isMutating = markKept.isPending || markReassigned.isPending || markDeleted.isPending;

  if (isAuthLoading) {
    return <Skeleton className="h-32" />;
  }
  if (user && !user.isPipelineAdmin) {
    return (
      <ErrorState
        title={t('common.accessDeniedTitle')}
        message={t('ownershipTransfers.accessDenied')}
      />
    );
  }

  const openConfirm = (mode: ConfirmMode, record: OwnershipTransferRecord): void => {
    setNewOwnerEmailInput('');
    setConfirmTarget({ mode, record });
  };

  const closeConfirm = (): void => {
    if (isMutating) return;
    setConfirmTarget(null);
    setNewOwnerEmailInput('');
  };

  const handleConfirm = (): void => {
    if (confirmTarget === null) return;
    const id = confirmTarget.record.id;
    if (confirmTarget.mode === 'kept') {
      markKept.mutate(
        { id },
        {
          onSuccess: () => {
            showToast('success', t('ownershipTransfers.markKeptSuccess'));
            setConfirmTarget(null);
          },
          onError: (error) => {
            showToast(
              'error',
              t('ownershipTransfers.markKeptError', { reason: formatError(error) }),
            );
          },
        },
      );
      return;
    }
    if (confirmTarget.mode === 'reassigned') {
      const newOwnerEmail = newOwnerEmailInput.trim();
      if (newOwnerEmail === '') return;
      markReassigned.mutate(
        { id, newOwnerEmail },
        {
          onSuccess: () => {
            showToast(
              'success',
              t('ownershipTransfers.markReassignedSuccess', { email: newOwnerEmail }),
            );
            setConfirmTarget(null);
            setNewOwnerEmailInput('');
          },
          onError: (error) => {
            showToast(
              'error',
              t('ownershipTransfers.markReassignedError', { reason: formatError(error) }),
            );
          },
        },
      );
      return;
    }
    markDeleted.mutate(
      { id },
      {
        onSuccess: () => {
          showToast('success', t('ownershipTransfers.markDeletedSuccess'));
          setConfirmTarget(null);
        },
        onError: (error) => {
          showToast(
            'error',
            t('ownershipTransfers.markDeletedError', { reason: formatError(error) }),
          );
        },
      },
    );
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl">
      <header className="space-y-2">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('ownershipTransfers.title')}
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)]">
          {t('ownershipTransfers.description')}
        </p>
      </header>

      {/* フィルタ */}
      <section className="glass-card p-6 space-y-3">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('ownershipTransfers.filterTitle')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input
            label={t('ownershipTransfers.originalOwnerEmail')}
            placeholder="alice@air-closet.com"
            value={originalOwnerEmail}
            onChange={(e) => {
              setOriginalOwnerEmail(e.target.value);
            }}
            aria-label="filter by originalOwnerEmail"
          />
          <Select
            label="reviewStatus"
            value={reviewStatus}
            onChange={(e) => {
              setReviewStatus(e.target.value as OwnershipTransferReviewStatus | '');
            }}
            options={[
              { value: '', label: t('common.unspecified') },
              { value: 'pending', label: 'pending' },
              { value: 'kept', label: 'kept' },
              { value: 'reassigned', label: 'reassigned' },
              { value: 'deleted', label: 'deleted' },
            ]}
            aria-label="filter by reviewStatus"
          />
          <Select
            label="resourceType"
            value={resourceType}
            onChange={(e) => {
              setResourceType(e.target.value as OwnershipTransferResourceType | '');
            }}
            options={[
              { value: '', label: t('common.unspecified') },
              { value: 'pipeline_config', label: 'pipeline_config' },
              { value: 'pipeline_api_key', label: 'pipeline_api_key' },
              { value: 'db_credential', label: 'db_credential' },
              { value: 'mcp_user_auth', label: 'mcp_user_auth' },
            ]}
            aria-label="filter by resourceType"
          />
        </div>
      </section>

      {/* 一覧 */}
      <section className="glass-card p-6 space-y-4">
        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
        )}
        {isError && (
          <ErrorState
            title={t('common.loadFailedTitle')}
            message={t('ownershipTransfers.loadFailed')}
            onRetry={() => {
              void refetch();
            }}
          />
        )}
        {!isLoading && !isError && (data?.records.length ?? 0) === 0 && (
          <p className="text-sm text-[var(--color-text-muted)]">{t('ownershipTransfers.empty')}</p>
        )}
        {!isLoading && !isError && (data?.records.length ?? 0) > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--color-text-secondary)] border-b border-[var(--color-border)]">
                  <th className="py-2 pr-3 font-medium">transferredAt</th>
                  <th className="py-2 pr-3 font-medium">
                    {t('ownershipTransfers.columnOriginalOwner')}
                  </th>
                  <th className="py-2 pr-3 font-medium">resource</th>
                  <th className="py-2 pr-3 font-medium">action</th>
                  <th className="py-2 pr-3 font-medium">status</th>
                  <th className="py-2 pr-3 font-medium">{t('ownershipTransfers.columnActions')}</th>
                </tr>
              </thead>
              <tbody>
                {data?.records.map((record) => (
                  <OwnershipTransferRow
                    key={record.id}
                    record={record}
                    onMark={(mode) => {
                      openConfirm(mode, record);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* 確定モーダル */}
      <Modal
        isOpen={confirmTarget !== null}
        onClose={closeConfirm}
        title={confirmTarget ? t(`ownershipTransfers.confirm.${confirmTarget.mode}.title`) : ''}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={closeConfirm} disabled={isMutating}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={confirmTarget?.mode === 'deleted' ? 'danger' : 'primary'}
              onClick={handleConfirm}
              loading={isMutating}
              disabled={
                isMutating ||
                (confirmTarget?.mode === 'reassigned' && newOwnerEmailInput.trim() === '')
              }
            >
              {confirmTarget ? t(`ownershipTransfers.confirm.${confirmTarget.mode}.action`) : ''}
            </Button>
          </div>
        }
      >
        {confirmTarget && <ConfirmBody target={confirmTarget} />}
        {confirmTarget?.mode === 'reassigned' && (
          <div className="mt-4">
            <Input
              label={t('ownershipTransfers.newOwnerEmail')}
              placeholder="bob@air-closet.com"
              value={newOwnerEmailInput}
              onChange={(e) => {
                setNewOwnerEmailInput(e.target.value);
              }}
              aria-label="new owner email"
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

interface ConfirmBodyProps {
  readonly target: ConfirmTarget;
}

/** @graph-connects none */
function ConfirmBody({ target }: ConfirmBodyProps) {
  const { t } = useTranslation();
  const { record, mode } = target;
  return (
    <div className="space-y-2 text-sm">
      <div>
        <span className="text-[var(--color-text-secondary)]">
          {t('ownershipTransfers.confirmOriginalOwner')}
        </span>
        <span className="font-mono">{record.originalOwnerEmail}</span>
        {record.originalOwnerNickname !== null && (
          <span className="text-[var(--color-text-muted)]"> ({record.originalOwnerNickname})</span>
        )}
      </div>
      <div>
        <span className="text-[var(--color-text-secondary)]">
          {t('ownershipTransfers.confirmTarget')}
        </span>
        <span className="font-mono">
          {RESOURCE_TYPE_LABEL[record.resourceType]} / {record.resourceId}
        </span>
      </div>
      <p className={mode === 'deleted' ? 'text-red-400' : 'text-[var(--color-text-muted)]'}>
        {t(`ownershipTransfers.confirm.${mode}.description`)}
      </p>
    </div>
  );
}

interface OwnershipTransferRowProps {
  readonly record: OwnershipTransferRecord;
  readonly onMark: (mode: ConfirmMode) => void;
}

/** @graph-connects none */
function OwnershipTransferRow({ record, onMark }: OwnershipTransferRowProps) {
  const { t } = useTranslation();
  const canMark = record.reviewStatus === 'pending';
  const canReassign =
    canMark &&
    record.transferredAction === 'transfer' &&
    REASSIGNABLE_RESOURCE_TYPES.has(record.resourceType);
  // mcp_user_auth の transfer record を UI から delete すると admin の CORTEX_API_KEY が
  // 失効するため、API 側で禁止している (orpc.ts)。UI 側でもボタンを出さない。
  const canDelete =
    canMark &&
    (record.transferredAction !== 'transfer' ||
      REASSIGNABLE_RESOURCE_TYPES.has(record.resourceType));
  return (
    <tr className="border-b border-[var(--color-border)]/40 hover:bg-[var(--color-bg-hover)]/30">
      <td className="py-2 pr-3 font-mono text-xs text-[var(--color-text-secondary)]">
        {new Date(record.transferredAt).toLocaleString()}
      </td>
      <td className="py-2 pr-3">
        <div className="font-mono text-xs">{record.originalOwnerEmail}</div>
        {record.originalOwnerNickname !== null && (
          <div className="text-[var(--color-text-muted)] text-xs">
            {record.originalOwnerNickname}
          </div>
        )}
      </td>
      <td className="py-2 pr-3">
        <div className="font-mono text-xs">{RESOURCE_TYPE_LABEL[record.resourceType]}</div>
        <div className="text-[var(--color-text-muted)] text-xs">{record.resourceId}</div>
      </td>
      <td className="py-2 pr-3">
        <Badge variant={record.transferredAction === 'transfer' ? 'info' : 'error'}>
          {record.transferredAction}
        </Badge>
      </td>
      <td className="py-2 pr-3">
        <Badge variant={REVIEW_STATUS_VARIANT[record.reviewStatus]}>
          {REVIEW_STATUS_LABEL[record.reviewStatus]}
        </Badge>
        {record.reviewedBy !== null && (
          <div className="text-[var(--color-text-muted)] text-xs mt-1">by {record.reviewedBy}</div>
        )}
      </td>
      <td className="py-2 pr-3">
        {canMark ? (
          <div className="flex gap-1 flex-wrap">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                onMark('kept');
              }}
            >
              kept
            </Button>
            {canReassign && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  onMark('reassigned');
                }}
              >
                reassign
              </Button>
            )}
            {canDelete && (
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  onMark('deleted');
                }}
              >
                delete
              </Button>
            )}
          </div>
        ) : (
          <span className="text-[var(--color-text-muted)] text-xs">
            {t('ownershipTransfers.settled')}
          </span>
        )}
      </td>
    </tr>
  );
}

/** @graph-connects none */
function formatError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
